/**
 * Environment Details Form Configuration
 * 
 * This file defines the form fields for each technology and integration type combination.
 * The form dynamically renders fields based on the selected technology and integration.
 */

// Technology types
export type TechnologyType = 
  | "Apple Wallet - University"
  | "Apple Wallet - Corporate"
  | "Google Wallet - University"
  | "Google Wallet - Corporate"
  | "App Based"
  | "OIP"
  | "HID Bridge";

// Integration types
export type IntegrationType = 
  | "App Provisioning"
  | "Web Provisioning"
  | "OIP Android Integration"
  | "OIP IOS Integration"
  | "Origo API"
  | "iOS SDK"
  | "Android SDK";
 
  

// Field definition for form rendering
export interface FormFieldConfig {
  name: string;
  label: string;
  type: "text" | "textarea" | "date" | "file";
  placeholder?: string;
  required?: boolean;
  helpText?: string;
}

// Form configuration by technology/integration combination
export type FormType = 
  | "APP_BASED_API"
  | "APP_BASED_SDK"
  | "WALLET_APP_SDK"
  | "WALLET_WEB_SDK"
  | "OIP_BRIDGE";

/**
 * Determines the form type based on technology and integration
 */
export function getFormType(technology: string, integration: string): FormType {
  const techLower = technology.toLowerCase();
  const integLower = integration.toLowerCase();
  
  // Wallet technologies
  const isWallet = techLower.includes("wallet");
  
  // App Based technology
  const isAppBased = techLower.includes("app based");
  
  // OIP or HID Bridge
  const isOIPOrBridge = techLower.includes("oip") || techLower.includes("hid bridge");
  
  if (isAppBased) {
    if (integLower.includes("api")) {
      return "APP_BASED_API";
    }
    // iOS and Android are SDK types
    return "APP_BASED_SDK";
  }
  
  if (isWallet) {
    if (integLower.includes("web")) {
      return "WALLET_WEB_SDK";
    }
    return "WALLET_APP_SDK";
  }
  
  // OIP and HID Bridge share the same form
  return "OIP_BRIDGE";
}

/**
 * Common fields shared across all form types
 */
const commonFields: FormFieldConfig[] = [
  {
    name: "end_customer_name",
    label: "End Customer Name",
    type: "text",
    placeholder: "Enter end customer name",
    required: true,
  },
  {
    name: "dev_team_size",
    label: "Dev Team Size",
    type: "text",
    placeholder: "Enter team size",
    required: true,
  },
  {
    name: "dev_team_location",
    label: "Dev Team Location",
    type: "text",
    placeholder: "Enter team location",
    required: true,
  },
  {
    name: "application_version",
    label: "Application Version",
    type: "text",
    placeholder: "Enter application version",
    required: true,
  },
  {
    name: "access_control_system",
    label: "Access Control System",
    type: "text",
    placeholder: "Enter access control system",
    required: true,
  },
  {
    name: "planned_production_launch",
    label: "Planned Production Launch Date",
    type: "date",
    placeholder: "dd/mm/yyyy",
    required: true,
  },
  {
    name: "end_user_usecase",
    label: "End-User Use Cases",
    type: "textarea",
    placeholder: "Describe the primary end-user use cases intended for this application",
    required: true,
  },
  {
    name: "sequence_flow_diagram",
    label: "Sequence Flow Diagram",
    type: "file",
    required: true,
  },
];

/**
 * Form fields for App Based - API integration
 * Note: application_name field is used with different labels based on form type
 */
const appBasedAPIFields: FormFieldConfig[] = [
  {
    name: "application_name",
    label: "App Name for backend System/IAM System/headend system (API)",
    type: "text",
    placeholder: "Enter API app name",
    required: true,
  },
  ...commonFields.slice(0, 5), // end_customer_name through access_control_system
  {
    name: "hid_origo_version",
    label: "Which version of HID Origo API currently using",
    type: "text",
    placeholder: "Enter HID Origo API version",
    required: true,
  },
  {
    name: "programming_language",
    label: "Programming Language used?",
    type: "text",
    placeholder: "Enter programming language",
    required: true,
  },
  {
    name: "hosting_environment",
    label: "Hosting Environment? (example: AWS, Azure, On-Premises)",
    type: "text",
    placeholder: "Enter hosting environment",
    required: true,
  },
  ...commonFields.slice(5), // planned_production_launch, end_user_usecase, sequence_flow_diagram
];

/**
 * Form fields for App Based - SDK (iOS/Android) integration
 * Note: application_name field is used with different labels based on form type
 */
const appBasedSDKFields: FormFieldConfig[] = [
  {
    name: "application_name",
    label: "App Name for SDK",
    type: "text",
    placeholder: "Enter SDK app name",
    required: true,
  },
  ...commonFields.slice(0, 5), // end_customer_name through access_control_system
  {
    name: "hid_origo_sdk_version",
    label: "Which version of HID Origo SDK currently using",
    type: "text",
    placeholder: "Enter HID Origo SDK version",
    required: true,
  },
  {
    name: "development_tools",
    label: "Development language/platform for SDK? (Kotlin, Java, Xamarin, Swift, Objective C)",
    type: "text",
    placeholder: "Enter development language/platform",
    required: true,
  },
  {
    name: "application_distribution",
    label: "Application Distribution: App Store/Playstore link or MDM, in-house",
    type: "text",
    placeholder: "Enter app distribution method",
    required: true,
  },
  commonFields[5], // planned_production_launch
  {
    name: "third_party_libraries",
    label: "Any third-party libraries are you using with the application?",
    type: "text",
    placeholder: "Enter third-party libraries",
    required: false,
  },
  {
    name: "device_compatibility_info",
    label: "How does the application inform people if their device is not on the device compatibility list?",
    type: "textarea",
    placeholder: "Describe device compatibility notification",
    helpText: "Reference: https://www.hidglobal.com/mobile-access-compatible-devices",
    required: true,
  },
  commonFields[6], // end_user_usecase
  {
    name: "mobile_id_provisioning",
    label: "Please provide details on how Mobile IDs be provisioned to users?",
    type: "textarea",
    placeholder: "Describe mobile ID provisioning process",
    required: true,
  },
  commonFields[7], // sequence_flow_diagram
];

/**
 * Form fields for Wallet - App SDK integration
 * Note: application_name for SDK, api_app_name for backend/API
 */
const walletAppSDKFields: FormFieldConfig[] = [
  {
    name: "application_name",
    label: "App Name for SDK",
    type: "text",
    placeholder: "Enter SDK app name",
    required: true,
  },
  {
    name: "api_app_name",
    label: "App Name for backend System/IAM System/headend system (API)",
    type: "text",
    placeholder: "Enter API app name",
    required: true,
  },
  ...commonFields.slice(0, 5), // end_customer_name through access_control_system
  {
    name: "hid_origo_sdk_version",
    label: "Which version of HID Origo SDK currently using",
    type: "text",
    placeholder: "Enter HID Origo SDK version",
    required: true,
  },
  {
    name: "hid_origo_version",
    label: "Which version of HID Origo API currently using",
    type: "text",
    placeholder: "Enter HID Origo API version",
    required: true,
  },
  {
    name: "programming_language",
    label: "Programming Language used for API Integration",
    type: "text",
    placeholder: "Enter programming language",
    required: true,
  },
  {
    name: "hosting_environment",
    label: "Hosting Environment for API integration? (for example: AWS, Azure, On-Premises)",
    type: "text",
    placeholder: "Enter hosting environment",
    required: true,
  },
  {
    name: "development_tools",
    label: "Development language/platform for SDK? (Kotlin, Java, Xamarin)",
    type: "text",
    placeholder: "Enter development language/platform",
    required: true,
  },
  {
    name: "third_party_libraries",
    label: "Any third-party libraries are you using with the application?",
    type: "text",
    placeholder: "Enter third-party libraries",
    required: false,
  },
  {
    name: "application_distribution",
    label: "Application Distribution: App Store/Playstore link or MDM, in-house",
    type: "text",
    placeholder: "Enter app distribution method",
    required: true,
  },
  commonFields[5], // planned_production_launch
  commonFields[6], // end_user_usecase
  commonFields[7], // sequence_flow_diagram
];

/**
 * Form fields for Wallet - Web SDK integration
 * Note: application_name for SDK, api_app_name for backend/API
 */
const walletWebSDKFields: FormFieldConfig[] = [
  {
    name: "application_name",
    label: "App Name for SDK",
    type: "text",
    placeholder: "Enter SDK app name",
    required: true,
  },
  {
    name: "api_app_name",
    label: "App Name for backend System/IAM System/headend system (API)",
    type: "text",
    placeholder: "Enter API app name",
    required: true,
  },
  ...commonFields.slice(0, 5), // end_customer_name through access_control_system
  {
    name: "hid_origo_version",
    label: "Which version of HID Origo API currently using",
    type: "text",
    placeholder: "Enter HID Origo API version",
    required: true,
  },
  {
    name: "programming_language",
    label: "Programming Language used for API Integration",
    type: "text",
    placeholder: "Enter programming language",
    required: true,
  },
  {
    name: "hosting_environment",
    label: "Hosting Environment for API integration? (for example: AWS, Azure, On-Premises)",
    type: "text",
    placeholder: "Enter hosting environment",
    required: true,
  },
  {
    name: "development_tools",
    label: "Development language/platform used in application developed? (For example: JavaScript, etc)",
    type: "text",
    placeholder: "Enter development language/platform",
    required: true,
  },
  {
    name: "deployment_method",
    label: "How will the application be deployed to the users post validation?",
    type: "textarea",
    placeholder: "Describe deployment method",
    required: true,
  },
  commonFields[5], // planned_production_launch
  commonFields[6], // end_user_usecase
  commonFields[7], // sequence_flow_diagram
];

/**
 * Form fields for OIP and HID Bridge
 * Note: application_name for SDK, api_app_name for backend/API
 */
const oipBridgeFields: FormFieldConfig[] = [
  {
    name: "application_name",
    label: "App Name for SDK",
    type: "text",
    placeholder: "Enter SDK app name",
    required: true,
  },
  {
    name: "api_app_name",
    label: "App Name for backend System/IAM System/headend system (API)",
    type: "text",
    placeholder: "Enter API app name",
    required: true,
  },
  ...commonFields.slice(0, 5), // end_customer_name through access_control_system
  {
    name: "hid_origo_sdk_version",
    label: "Which version of HID Origo SDK currently using",
    type: "text",
    placeholder: "Enter HID Origo SDK version",
    required: true,
  },
  {
    name: "hid_origo_version",
    label: "Which version of HID Origo API currently using",
    type: "text",
    placeholder: "Enter HID Origo API version",
    required: true,
  },
  {
    name: "programming_language",
    label: "Programming Language used for API Integration",
    type: "text",
    placeholder: "Enter programming language",
    required: true,
  },
  {
    name: "hosting_environment",
    label: "Hosting Environment for API integration? (for example: AWS, Azure, On-Premises)",
    type: "text",
    placeholder: "Enter hosting environment",
    required: true,
  },
  {
    name: "development_tools",
    label: "Development language/platform for SDK? (Kotlin, Java, Xamarin)",
    type: "text",
    placeholder: "Enter development language/platform",
    required: true,
  },
  {
    name: "third_party_libraries",
    label: "Any third-party libraries are you using with the application?",
    type: "text",
    placeholder: "Enter third-party libraries",
    required: false,
  },
  {
    name: "application_distribution",
    label: "Application Distribution: App Store/Playstore link or MDM, in-house",
    type: "text",
    placeholder: "Enter app distribution method",
    required: true,
  },
  commonFields[5], // planned_production_launch
  commonFields[6], // end_user_usecase
  commonFields[7], // sequence_flow_diagram
];

/**
 * Map of form types to their field configurations
 */
const formFieldsMap: Record<FormType, FormFieldConfig[]> = {
  APP_BASED_API: appBasedAPIFields,
  APP_BASED_SDK: appBasedSDKFields,
  WALLET_APP_SDK: walletAppSDKFields,
  WALLET_WEB_SDK: walletWebSDKFields,
  OIP_BRIDGE: oipBridgeFields,
};

/**
 * Get form fields for a specific technology and integration combination
 */
export function getFormFields(technology: string, integration: string): FormFieldConfig[] {
  const formType = getFormType(technology, integration);
  return formFieldsMap[formType] || [];
}

/**
 * Get form title based on technology and integration
 */
export function getFormTitle(technology: string, integration: string, isHIDUser: boolean): string {
  const action = isHIDUser ? "Verify" : "Complete";
  return `${action} Environment Details`;
}

/**
 * Check if a technology is a wallet type
 */
export function isWalletTechnology(technology: string): boolean {
  return technology.toLowerCase().includes("wallet");
}

/**
 * Check if a technology is App Based
 */
export function isAppBasedTechnology(technology: string): boolean {
  return technology.toLowerCase().includes("app based");
}

/**
 * Get available integration types for a technology
 */
export function getIntegrationTypes(technology: string): string[] {
  if (isWalletTechnology(technology)) {
    return ["App SDK", "Web SDK"];
  }
  if (isAppBasedTechnology(technology)) {
    return ["API", "iOS", "Android"];
  }
  // OIP and HID Bridge don't have specific integration types
  return [];
}

